import dayjs, { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { Job, JobStatus } from '@core/generated/gql-types';
import { useJobsTable } from '../job/useJobsTable';

/**
 * Hook to get prior week job data for comparison metrics.
 * Note: This filters the provided jobs list by the prior week date range.
 * For accurate prior week data, the jobs list should include jobs from both weeks.
 */
export const usePriorWeekData = (
  allJobs: Job[],
  timeframeStartDate: Dayjs | undefined,
) => {
  // Calculate the week before the current selected week
  const priorWeekSelectedDate = useMemo((): Dayjs | undefined => {
    if (!timeframeStartDate) return undefined;
    return timeframeStartDate.subtract(6, 'day');
  }, [timeframeStartDate]);

  // Fetch jobs for the prior week to compare against current week
  const { allFilteredJobs: priorWeekAllFilteredJobs } = useJobsTable(
    allJobs,
    priorWeekSelectedDate,
  );

  // Filled jobs from the prior week
  const priorWeekFilledJobs = useMemo((): Job[] => {
    return priorWeekAllFilteredJobs.filter(
      (job) =>
        job.itemStatus === JobStatus.Filled ||
        job.itemStatus === JobStatus.Completed,
    );
  }, [priorWeekAllFilteredJobs]);

  // Unfilled jobs from the prior week
  const priorWeekUnfilledJobs = useMemo((): Job[] => {
    return priorWeekAllFilteredJobs.filter(
      (job) => job.itemStatus === JobStatus.Unfilled,
    );
  }, [priorWeekAllFilteredJobs]);

  // Percentage of jobs filled in the prior week
  const priorWeekPercentageFilled = useMemo((): number => {
    const totalItems =
      priorWeekFilledJobs.length + priorWeekUnfilledJobs.length;
    return (priorWeekFilledJobs.length / (totalItems || 1)) * 100;
  }, [priorWeekFilledJobs, priorWeekUnfilledJobs]);

  return {
    priorWeekAllFilteredJobs,
    priorWeekFilledJobs,
    priorWeekUnfilledJobs,
    priorWeekPercentageFilled,
  };
};
